import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as ArrowDown16 } from 'assets/svg/ArrowDown16.svg'
import { ReactComponent as ArrowRight16 } from 'assets/svg/ArrowRight16.svg'
import { useRFPDetailsPopupContext } from '../Providers/RFPDetailsPopupProvider'
import { useDispatch } from 'react-redux'
import { useEffect, useMemo, useRef, useState } from 'react'
import { DEFAULT_PAGE } from 'components/Table/constants'
import { useParams } from 'react-router-dom'
import { useDebounce } from 'hooks/hooks'
import { setLoading } from 'redux/store/common/slice'
import { Typography, TYPOGRAPHY_WEIGHT } from 'components/Typography'
import cn from 'classnames'
import { Input } from '../../../../components/Input'
import { Button, BUTTON_TYPES } from '../../../../components/Button'
import { Table } from 'components/Table'
import axios, { CancelTokenSource } from 'axios'
import { BTN_TXT, PLACEHOLDERS } from '../../../../constants'
import { isTableHasParams } from '../../../../helper/common'
import { useRFPDetailsContext } from '../Providers/RFPDetailsContextProvider'
import { IRfpHealthSystemWithHospitals } from './types'
import {
  THealthSystemTableRequestData,
  THealthSystemUsersParams
} from '../../../HealthSystemUsers/types'
import { columns } from './constants'
import { DeleteHealthSystemPopup } from './DeleteHealthSystemPopup'
import { getRfpHealthSystems } from '../api'
import './style.scss'
import { AddHealthSystemPopup } from './AddHelthSystemPopup'
import { RFP_STATUSES } from '../../constants'

export const Facilities = () => {
  const { addHealthSystemPopup } = useRFPDetailsPopupContext()
  const dispatch = useDispatch()
  const { state, actions } = useRFPDetailsContext()
  const { handleGetRfpData } = actions
  const { canEditRfp, data, canSeeHealthSystem } = state
  const [totalNumber, setTotalNumber] = useState(0)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState<
    IRfpHealthSystemWithHospitals[]
  >([])

  const canAddHealthSystemPopup = useMemo(() => {
    return (
      (data.status === RFP_STATUSES.CREATED ||
        data.status === RFP_STATUSES.IN_CS_QUEUE ||
        data.status === RFP_STATUSES.WAITING_FOR_ASSIGNMENT) &&
      canEditRfp
    )
  }, [canEditRfp, data.status])
  const [searchValue, setSearchValue] = useState('')
  const [tableData, setTableData] = useState<IRfpHealthSystemWithHospitals[]>(
    []
  )
  const [sortParams, setSortParams] = useState<THealthSystemTableRequestData>()
  const [firstLoad, setFirstLoad] = useState(true)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])

  const { id } = useParams()

  const tableDataTokenRef = useRef<CancelTokenSource>()
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const debouncedSearch = useDebounce(searchValue, 500)
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => {
        getTableData({ ...sortParams, page: { ...DEFAULT_PAGE } })
        setPageInfo({ ...DEFAULT_PAGE })
      },
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: THealthSystemTableRequestData) => {
    dispatch(setLoading(true))
    if (firstLoad) {
      setFirstLoad(false)
    }
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: THealthSystemUsersParams = { ...filters }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }

    if (searchValue) {
      params.search = searchValue
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    getRfpHealthSystems(id as string, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            key: i.uuid,
            hospital_level: false,
            children: i.hospitals.map((hospital) => {
              return {
                ...hospital,
                key: hospital.uuid,
                hospital_level: true
              }
            })
          }))
        )
        setSelectedRowKeys([])
        setSelectedRows([])
        setTotalNumber(resp.data.count)
        handleGetRfpData()
      })
      .finally(() => dispatch(setLoading(false)))
  }

  useEffect(() => {
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData[0]?.uuid as string])

  const disableDeleteButton = useMemo(() => {
    return (
      tableData.length === 1 &&
      tableData[0]?.children.length ===
        selectedRows.filter((selectedRow) => selectedRow.hospital_level).length
    )
  }, [tableData, selectedRows])

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      page
    })
    setPageInfo(page)
  }
  const handleSelectRow = (selectedRowKeys, selectedRow) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRow)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }

  const updateHSList = () =>
    getTableData({
      ...sortParams
    })

  const displayData = useMemo(
    () =>
      canSeeHealthSystem
        ? tableData
        : (tableData.flatMap(
            (hs) => hs.children
          ) as IRfpHealthSystemWithHospitals[]),
    [canSeeHealthSystem, tableData]
  )

  return (
    <div className="table-wrapper">
      <div className="table-wrapper__header">
        {!!selectedRowKeys.length ? (
          <Typography.Body1
            className="table-wrapper__select-title"
            weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
          >
            Selected {selectedRowKeys.length} of {totalNumber}
          </Typography.Body1>
        ) : (
          <Input
            className="allow-clear"
            propsInput={{
              allowClear: { clearIcon: <Close16 /> },
              className: 'rfp-hs-users_search',
              placeholder: PLACEHOLDERS.SEARCH,
              prefix: <Search16 />,
              value: searchValue,
              onChange: (e) => setSearchValue(e.target.value)
            }}
          />
        )}
        <div className="table-wrapper__header_buttons row">
          {canAddHealthSystemPopup && (
            <>
              {!selectedRowKeys.length && (
                <Button
                  type={BUTTON_TYPES.PRIMARY}
                  icon={<Plus16 />}
                  onClick={addHealthSystemPopup.actions.open}
                  upperCase
                >
                  {canSeeHealthSystem
                    ? BTN_TXT.ADD_SYSTEM
                    : BTN_TXT.ADD_HOSPITAL}
                </Button>
              )}
              <DeleteHealthSystemPopup
                updateHSList={updateHSList}
                selectedHS={selectedRows}
                disableButton={disableDeleteButton}
              />
            </>
          )}
        </div>
      </div>
      <Table<IRfpHealthSystemWithHospitals>
        dataSource={displayData}
        columns={columns({
          showAmountOfHospitals: true,
          canSeeHealthSystem
        })}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        className={cn('rfp-vendors-data-list-table rfp-hs-list-table', {
          selectable: canAddHealthSystemPopup && canSeeHealthSystem
        })}
        onChangePage={handleChangePageSize}
        onChange={handleTableChange}
        hasSearchOrFilters={isTableHasParams(debouncedSearch, false)}
        expandable={
          canSeeHealthSystem
            ? {
                defaultExpandAllRows: false,
                columnWidth: 40,
                expandedRowKeys,
                onExpand: (expanded, record) => {
                  if (expanded) setExpandedRowKeys([record.uuid])
                  else setExpandedRowKeys([])
                },
                expandIcon: ({ expanded, onExpand, record, expandable }) =>
                  expandable ? (
                    expanded ? (
                      <div
                        className="expand-wrapper row gap-12 align-center"
                        onClick={(e) => onExpand(record, e)}
                      >
                        <ArrowDown16 />
                      </div>
                    ) : (
                      <div
                        className="expand-wrapper row gap-12 align-center"
                        onClick={(e) => onExpand(record, e)}
                      >
                        <ArrowRight16 />
                      </div>
                    )
                  ) : (
                    <></>
                  )
              }
            : undefined
        }
        createButton={
          canAddHealthSystemPopup
            ? {
                buttonText: canSeeHealthSystem
                  ? BTN_TXT.ADD_SYSTEM
                  : BTN_TXT.ADD_HOSPITAL,
                availability: true,
                action: addHealthSystemPopup.actions.open
              }
            : undefined
        }
        rowSelection={
          canAddHealthSystemPopup
            ? {
                selectedRowKeys,
                onChange: handleSelectRow,
                hideSelectAll: false,
                checkStrictly: false,
                getCheckboxProps: ({ hospital_level }) => {
                  return {
                    disabled: false ? !hospital_level : false
                  }
                }
              }
            : undefined
        }
      />
      <AddHealthSystemPopup
        updateList={updateHSList}
        canSeeHealthSystem={canSeeHealthSystem}
      />
    </div>
  )
}
